import { z } from 'zod';
import { BRANDS } from './brands.js';
import type { Draft, Mailbox, Folder, ThreadDetail, ThreadPage, OutboxEntry } from './mail.js';
import type { RecipientWarning } from './safeguards.js';

export const brandIdSchema = z.enum(BRANDS);

export const emailAddressSchema = z
  .object({
    address: z.string().trim().min(3).email(),
    name: z.string().trim().max(200).optional(),
  })
  .strict();

export const attachmentUploadSchema = z
  .object({
    name: z.string().trim().min(1).max(255),
    mimeType: z.string().trim().min(3).max(255),
    content: z.string().max(25 * 1024 * 1024),
  })
  .strict();

export const brandChangeAcknowledgementSchema = z
  .object({ fromBrand: brandIdSchema, toBrand: brandIdSchema })
  .strict();

/**
 * Strict on purpose. An unknown key is a client that thinks it can say something
 * the protocol does not allow — most importantly, who the message is from.
 */
export const draftMessageSchema = z
  .object({
    to: z.array(emailAddressSchema),
    cc: z.array(emailAddressSchema).optional(),
    bcc: z.array(emailAddressSchema).optional(),
    subject: z.string().max(998),
    bodyHtml: z.string().max(2 * 1024 * 1024),
    attachments: z.array(attachmentUploadSchema).optional(),
    inReplyToThreadId: z.string().min(1).optional(),
  })
  .strict();

export const outgoingMessageSchema = draftMessageSchema
  .extend({
    to: z.array(emailAddressSchema).min(1),
    acknowledgedBrandChange: brandChangeAcknowledgementSchema.optional(),
    draftId: z.string().min(1).optional(),
  })
  .strict();

export const threadMutationSchema = z
  .object({
    unread: z.boolean().optional(),
    flagged: z.boolean().optional(),
    folderId: z.string().min(1).optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'No changes given.' });

export const threadQuerySchema = z
  .object({
    folderId: z.string().min(1).optional(),
    search: z.string().trim().max(200).optional(),
    unreadOnly: z.coerce.boolean().optional(),
    flaggedOnly: z.coerce.boolean().optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    offset: z.coerce.number().int().min(0).optional(),
  })
  .strict();

export const recipientWarningRequestSchema = z
  .object({ recipients: z.array(z.string().trim().min(1)).max(200) })
  .strict();

export interface MailboxesResponse {
  readonly mailboxes: readonly Mailbox[];
}
export interface FoldersResponse {
  readonly folders: readonly Folder[];
}
export type ThreadsResponse = ThreadPage;
export type ThreadResponse = ThreadDetail;
export interface OutboxResponse {
  readonly entries: readonly OutboxEntry[];
}
export interface SendResponse {
  readonly entry: OutboxEntry;
  /** Seconds remaining in which the send can still be undone. */
  readonly undoSeconds: number;
}
export interface DraftResponse {
  readonly draft: Draft;
}
export interface RecipientWarningResponse {
  readonly warnings: readonly RecipientWarning[];
}
export interface HealthResponse {
  readonly status: 'ok';
  readonly backend: string;
}

/**
 * The mail server checks the password itself, so the BFF holds no credential of its
 * own and cannot open a mailbox until its owner has signed in.
 */
export interface SignInState {
  /** Addresses this person holds, and whether each is currently open. */
  readonly mailboxes: readonly { readonly address: string; readonly unlocked: boolean }[];
  /** True when every mailbox is open. */
  readonly unlocked: boolean;
}

export interface SignInRequest {
  readonly password: string;
  /** The mailbox to open, when the request carries no session yet. */
  readonly address?: string;
}

/** Why a sign-in failed, when it did. */
export type SignInFailure =
  /** The mail server refused the password. */
  | 'REJECTED'
  /** The mail server could not be reached, or answered with a fault. */
  | 'UNAVAILABLE';

/**
 * Every mail route is scoped to a mailbox. The sending identity is a path segment,
 * which is what makes "send as the other brand" unrepresentable rather than merely
 * discouraged.
 */
export const apiPaths = {
  health: () => '/api/health',
  mailboxes: () => '/api/mailboxes',
  folders: (mailboxId: string) => `/api/mailboxes/${encodeURIComponent(mailboxId)}/folders`,
  threads: (mailboxId: string) => `/api/mailboxes/${encodeURIComponent(mailboxId)}/threads`,
  thread: (mailboxId: string, threadId: string) =>
    `/api/mailboxes/${encodeURIComponent(mailboxId)}/threads/${encodeURIComponent(threadId)}`,
  attachment: (mailboxId: string, messageId: string, attachmentId: string) =>
    `/api/mailboxes/${encodeURIComponent(mailboxId)}/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`,
  send: (mailboxId: string) => `/api/mailboxes/${encodeURIComponent(mailboxId)}/send`,
  drafts: (mailboxId: string) => `/api/mailboxes/${encodeURIComponent(mailboxId)}/drafts`,
  draft: (mailboxId: string, draftId: string) =>
    `/api/mailboxes/${encodeURIComponent(mailboxId)}/drafts/${encodeURIComponent(draftId)}`,
  recipientWarnings: (mailboxId: string) =>
    `/api/mailboxes/${encodeURIComponent(mailboxId)}/recipient-warnings`,
  signIn: () => '/api/auth/session',
  outbox: () => '/api/outbox',
  cancelSend: (entryId: string) => `/api/outbox/${encodeURIComponent(entryId)}/cancel`,
} as const;
